import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchCustomField,
  setDestroy,
} from "containers/custom-field/custom-field-container.slice";
import {
  createCustomField,
  updateCustomField,
} from "services/custom-field.service";
import {
  CatalogTable,
  ChangeLink,
  EditPanel,
  SettingsCard,
  Toggle,
  useSaveWithToast,
  useScrollIntoViewOnOpen,
} from "containers/settings/ui";
import {
  AddButton,
  Field,
  FIELD_INPUT_CLASS,
  PanelHeading,
} from "containers/settings/catalog-form-parts";

// The kinds of field a company can add, in the order the type list shows them.
const FIELD_TYPES = [
  { value: "text", labelKey: "custom_field_type_text" },
  { value: "number", labelKey: "custom_field_type_number" },
  { value: "date", labelKey: "custom_field_type_date" },
  { value: "select", labelKey: "custom_field_type_select" },
  { value: "checkbox", labelKey: "custom_field_type_checkbox" },
];

type FieldOption = { label: string; value: string };

type CustomFieldDraft = {
  id?: string;
  label: string;
  fieldType: string;
  /** Only used by the "select" type. */
  options: FieldOption[];
  isRequired: boolean;
  isVisibleInList: boolean;
};

const NEW_CUSTOM_FIELD: CustomFieldDraft = {
  label: "",
  fieldType: "text",
  options: [],
  isRequired: false,
  isVisibleInList: false,
};

type OptionsEditorProps = {
  options: FieldOption[];
  onChange: (options: FieldOption[]) => void;
};

/** The choices of a "select" field: one text box each, with a way to add and remove. */
function OptionsEditor({ options, onChange }: OptionsEditorProps) {
  const { t } = useTranslation();

  const renameOption = (index: number, text: string) =>
    onChange(
      options.map((option, i) =>
        i === index ? { label: text, value: text } : option
      )
    );

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">
        {t("custom_field_options")}
      </p>
      {options.map((option, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            aria-label={`${t("custom_field_option")} ${index + 1}`}
            className={FIELD_INPUT_CLASS}
            value={option.label}
            onChange={(e) => renameOption(index, e.target.value)}
            autoComplete="off"
          />
          <button
            type="button"
            aria-label={t("custom_field_remove_option")}
            onClick={() => onChange(options.filter((_, i) => i !== index))}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...options, { label: "", value: "" }])}
        className="inline-flex items-center gap-1 rounded-md px-1 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <Plus aria-hidden className="size-4" />
        {t("custom_field_add_option")}
      </button>
    </div>
  );
}

type SwitchRowProps = {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

function SwitchRow({ label, hint, checked, onChange }: SwitchRowProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-gray-900">{label}</div>
        <p className="text-xs text-gray-600">{hint}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

type CustomFieldEditPanelProps = {
  saved: CustomFieldDraft;
  companyId: string;
  onSaved: () => void;
  onCancel: () => void;
};

function CustomFieldEditPanel({
  saved,
  companyId,
  onSaved,
  onCancel,
}: CustomFieldEditPanelProps) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const [draft, setDraft] = useState(saved);
  const [triedToSave, setTriedToSave] = useState(false);

  const label = draft.label.trim();
  const isSelect = draft.fieldType === "select";
  const filledOptions = draft.options.filter(
    (option) => option.label.trim() !== ""
  );
  const isIncomplete = !label || (isSelect && filledOptions.length === 0);

  const changeType = (fieldType: string) =>
    setDraft({
      ...draft,
      fieldType,
      options: fieldType === "select" ? [{ label: "", value: "" }] : [],
    });

  const submit = async () => {
    setTriedToSave(true);
    if (isIncomplete) return;

    // Only document fields exist: no other kind has anything that reads it.
    const payload = {
      id: draft.id,
      label,
      type: "file",
      fieldType: draft.fieldType,
      fieldOptions: isSelect ? filledOptions : null,
      isRequired: draft.isRequired,
      isVisibleInList: draft.isVisibleInList,
    };
    const wasSaved = await save(() =>
      draft.id
        ? updateCustomField(payload, companyId)
        : createCustomField(payload, companyId)
    );
    if (wasSaved) onSaved();
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onCancel}
      saving={saving}
      error={triedToSave && isIncomplete ? t("requiredFields") : error}
    >
      <PanelHeading>
        {saved.id
          ? `${t("settings_change")}: ${saved.label}`
          : t("custom_field_add")}
      </PanelHeading>
      <div className="space-y-3">
        <Field htmlFor="custom-field-label" label={t("custom_field_label")}>
          <input
            id="custom-field-label"
            className={FIELD_INPUT_CLASS}
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            autoComplete="off"
            autoFocus
          />
        </Field>
        <Field htmlFor="custom-field-type" label={t("custom_field_type")}>
          <select
            id="custom-field-type"
            className={FIELD_INPUT_CLASS}
            value={draft.fieldType}
            onChange={(e) => changeType(e.target.value)}
          >
            {FIELD_TYPES.map((fieldType) => (
              <option key={fieldType.value} value={fieldType.value}>
                {t(fieldType.labelKey)}
              </option>
            ))}
          </select>
        </Field>
        {isSelect && (
          <OptionsEditor
            options={draft.options}
            onChange={(options) => setDraft({ ...draft, options })}
          />
        )}
        <SwitchRow
          label={t("custom_field_required")}
          hint={t("custom_field_required_hint")}
          checked={draft.isRequired}
          onChange={(isRequired) => setDraft({ ...draft, isRequired })}
        />
        <SwitchRow
          label={t("custom_field_visible_in_list")}
          hint={t("custom_field_visible_in_list_hint")}
          checked={draft.isVisibleInList}
          onChange={(isVisibleInList) =>
            setDraft({ ...draft, isVisibleInList })
          }
        />
      </div>
    </EditPanel>
  );
}

function CustomFieldContainer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { collection, isLoading, isError } = useAppSelector(
    (state) => state.customFieldReducer
  );
  const [editing, setEditing] = useState<CustomFieldDraft | null>(null);
  const panelRef = useScrollIntoViewOnOpen(editing);

  useEffect(() => {
    if (companyId) dispatch(fetchCustomField(companyId));
    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, dispatch]);

  if (!companyId) return null;

  const startEdit = (id: string) => {
    const row = collection.find((customField) => customField.id === id);
    if (row) {
      setEditing({
        id: row.id,
        label: row.label,
        fieldType: row.fieldType,
        options: row.fieldOptions ?? [],
        isRequired: row.isRequired,
        isVisibleInList: row.isVisibleInList,
      });
    }
  };

  const closeAndReload = () => {
    setEditing(null);
    dispatch(fetchCustomField(companyId));
  };

  const typeName = (fieldType: string) => {
    const known = FIELD_TYPES.find((type) => type.value === fieldType);
    return known ? t(known.labelKey) : fieldType;
  };

  return (
    <SettingsCard
      title={t("settings_nav_custom_fields_label")}
      description={t("custom_field_card_description")}
      actions={
        <AddButton
          label={t("custom_field_add")}
          onClick={() => setEditing(NEW_CUSTOM_FIELD)}
        />
      }
    >
      {editing && (
        <div ref={panelRef} className="mb-4">
          <CustomFieldEditPanel
            key={editing.id ?? "new"}
            saved={editing}
            companyId={companyId}
            onSaved={closeAndReload}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}
      <CatalogTable
        columns={[
          { key: "label", header: t("custom_field_label") },
          { key: "fieldType", header: t("custom_field_type") },
          { key: "isRequired", header: t("custom_field_required") },
          { key: "isVisibleInList", header: t("custom_field_visible_in_list") },
        ]}
        rows={collection.map((customField) => ({
          id: customField.id,
          cells: {
            label: <span className="font-medium">{customField.label}</span>,
            fieldType: typeName(customField.fieldType),
            isRequired: customField.isRequired ? t("yes") : t("no"),
            isVisibleInList: customField.isVisibleInList ? t("yes") : t("no"),
          },
        }))}
        renderRowAction={(id) => <ChangeLink onClick={() => startEdit(id)} />}
        empty={t("custom_field_empty")}
        isLoading={isLoading}
        loadError={isError ? t("settings_load_failed") : undefined}
      />
    </SettingsCard>
  );
}

export default CustomFieldContainer;
